<template>
  <mailbox-settings-layout :name="name">
    <el-card class="mailbox-settings-saved-replies">
      <template #header>
        <h3>Saved Replies</h3>
      </template>
      <entity-settings-loading v-if="isLoading" />
      <template v-else>
        <p>
          Create <strong>Saved Replies</strong> to respond efficiently to your customers’ most common questions.
          Saved Replies can be accessed from the <i class="ddi ddi-saved-replies in-text" /> button when replying to customers.
        </p>
        <div class="dd-divider" />
        <div class="row">
          <div class="col-12 replies-list">
            <div class="replies-list__header">
              <h4>Replies</h4>
              <dd-icon-button 
                :is-disabled="isSubmitting" 
                icon="plus" 
                type="circle" 
                size="medium" 
                title="Add saved reply"
                @click="beginCreateReply()" 
              />
            </div>
            <div v-if="hasSavedReplies">
              <mailbox-settings-saved-replies-reply
                v-for="savedReply in savedReplies"
                :id="savedReply.id"
                :key="savedReply.id"
                :title="savedReply.title"
                :disabled="isSubmitting"
                @edit="beginEditReply(savedReply.id)"
                @remove="removeReply(savedReply.id)"
              />
            </div>
            <p v-else class="replies-list__empty-message">
              There are no saved replies for this mailbox. 
              <a href="#" class="just-a-link" @click.stop.prevent="beginCreateReply()">Add your first</a>!
            </p>
          </div>
        </div>
        
        <!-- Modal -->
        <dd-modal
          v-model:visible="modalOpened"
          :title="(isEditing)? `Edit Reply` : `New Reply`"
          @handle-close="closeModal"
        >
          <el-form ref="upsertReplyForm" :model="upsertingSavedReply" :rules="rules" label-position="top">
            <!-- Title -->
            <el-form-item label="Title" prop="title">
              <el-input
                ref="savedReplyForm_Title"
                v-model="upsertingSavedReply.title"
                placeholder="e.g. Feature Request Reply"
                maxlength="100"
              />
            </el-form-item>

            <!-- Reply -->
            <el-form-item label="Reply" prop="reply">
              <editor
                v-model="upsertingSavedReply.reply"
                :variables="savedReplyVars"
                fixed-height
                placeholder="Write your reply..."
              />
            </el-form-item>
          </el-form>
          <template #footer>
            <span>
              <dd-button type="text" @click="closeModal">Cancel</dd-button>
              <dd-button :color="ColorAssistant.MailboxColor" @click="upsertReply">{{ (isEditing)? `Update Reply` : `Add Reply` }}</dd-button>
            </span>
          </template>
        </dd-modal>
      </template>
    </el-card>
  </mailbox-settings-layout>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import MailboxSettingsLayout from '@/layouts/mailbox-settings-layout.vue'

/* Mixins */

/* Components */
import EntitySettingsLoading from '@/layouts/entity-settings-loading.vue'
import MailboxSettingsSavedRepliesReply from './components/mailbox-settings-saved-replies-reply.vue'

/* Helpers */
import ColorAssistant from '@/helpers/color-assistant'

/* Data types */
import {
  IMailboxesOutputSavedRepliesSavedReply,
  INameAndDescription,
} from '@/api/types/response.types'
import {
  IMailboxesInputCreateSavedReply,
  IMailboxesInputUpdateSavedReply,
} from '@/api/types/request.types'

/* API */
import MailboxesAPI from '@/api/modules/mailboxes.api'

export default defineComponent({
  name: 'MailboxSettingsSavedReplies',

  components: {
    MailboxSettingsLayout,
    MailboxSettingsSavedRepliesReply,
    EntitySettingsLoading,
  },

  data() {
    return {
      ColorAssistant,
      isLoading: true,
      isSubmitting: false,
      editingID: null as any,
      name: '',
      savedReplyVars: [] as INameAndDescription[],
      savedReplies: [] as IMailboxesOutputSavedRepliesSavedReply[],
      upsertingSavedReply: {} as IMailboxesInputUpdateSavedReply,
      modalOpened: false,
      rules: {
        title: [
          {
            required: true,
            trigger: 'submit',
            message: 'Title is required',
          },
        ],
        reply: [
          {
            required: true,
            trigger: 'submit',
            message: 'Reply is required',
          },
        ],
      },
    }
  },

  head(): any {
    return {
      title: `Saved Replies - ${this.name}`,
    }
  },

  computed: {
    id(): number {
      return parseInt(this.$route.params.id, 10)
    },
    isEditing(): boolean {
      return this.editingID != null
    },
    hasSavedReplies(): boolean {
      return this.savedReplies.length > 0
    },
  },

  watch: {
    modalOpened(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          // @ts-ignore
          this.$refs.savedReplyForm_Title.focus()
        })
      }
    },
  },

  async created() {
    this.isLoading = true
    await this.getSavedRepliesInfo()
    this.isLoading = false
  },

  methods: {
    getSavedReply(id: number) {
      return this.savedReplies.find(sr => sr.id === id)
    },
    async getSavedRepliesInfo() {
      try {
        const response = await MailboxesAPI.get_SavedReplies(this.id)
        this.name = response.name
        this.savedReplyVars = response.listSavedReplyVars
        this.savedReplies = response.listSavedReplies
      } catch (error) {
        this.$error.emit(error)
      }
    },
    async upsertReply() {
      // @ts-ignore
      this.$refs.upsertReplyForm.validate((valid: boolean) => {
        if (valid) {
          this.isEditing ? this.updateSavedReply() : this.createSavedReply()
          this.closeModal()
        }
      })
    },

    beginEditReply(id: number): void {
      this.upsertingSavedReply = this.getSavedReply(id)
      this.modalOpened = true
      this.editingID = id
    },

    beginCreateReply(): void {
      this.upsertingSavedReply = {} as IMailboxesInputUpdateSavedReply
      this.modalOpened = true
      this.editingID = null
    },

    async removeReply(id: number) {
      await this.$confirm(
        `Are you sure you want to delete this saved reply?<br />
         This cannot be undone.`,
        {
          dangerouslyUseHTMLString: true,
          center: true,
          confirmButtonText: 'Yes, Delete',
          confirmButtonClass: 'el-button--strawberry',
          cancelButtonText: 'Cancel',
          cancelButtonClass: 'el-button--text',
          type: 'warning',
          title: `Delete Saved Reply`,
        },
      )
      await this.deleteSavedReply(id)
    },

    closeModal(): void {
      this.modalOpened = false
      // @ts-ignore
      this.$refs.upsertReplyForm.clearValidate()
    },

    async createSavedReply() {
      try {
        this.isSubmitting = true
        const data = this.upsertingSavedReply as IMailboxesInputCreateSavedReply
        const response = await MailboxesAPI.post_CreateSavedReply(this.id, data)
        this.$messaging.showSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        await this.getSavedRepliesInfo()
        this.isSubmitting = false
      }
    },

    async updateSavedReply() {
      try {
        this.isSubmitting = true
        const data = this.upsertingSavedReply as IMailboxesInputUpdateSavedReply
        const response = await MailboxesAPI.put_UpdateSavedReply(
          this.id,
          this.editingID,
          data,
        )
        this.$messaging.showSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        await this.getSavedRepliesInfo()
        this.isSubmitting = false
      }
    },

    async deleteSavedReply(id: number) {
      try {
        this.isSubmitting = true
        const response = await MailboxesAPI.delete_DeleteSavedReply(this.id, id)
        this.$messaging.showSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        await this.getSavedRepliesInfo()
        this.isSubmitting = false
      }
    },
  },
})
</script>

<style lang="scss">
.mailbox-settings-saved-replies {
  .in-text {
    font-size: 14px;
    padding: 0 2px;
  }

  .replies-list {
    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 10px;
    }

    &__empty-message {
      color: var(--color-text-third);
      padding-bottom: 20px;
    }
  }
}
</style>
